import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  Star,
  Users,
  Clock,
  CheckCircle2,
  UserX,
  Activity,
  BarChart3,
  School,
  Layers
} from "lucide-react";

import { BuscaNome, SemResultadoBusca } from "../../components/BuscaNome";
import { NotaBarraLista } from "../../components/charts/Charts";
import { EvolucaoTemporal } from "../../components/charts/EvolucaoTemporal";
import { FaixaDonutCard } from "../../components/charts/FaixaDonutCard";
import { EngajamentoCell } from "../../components/layout/EngajamentoCell";
import { KpiCard } from "../../components/layout/KpiCard";
import { NotaCell } from "../../components/layout/NotaCell";
import { RankingTabela } from "../../components/layout/RankingTabela";
import PortalIE from "../../components/ui/PortalIE";
import {
  Card,
  SectionHead,
  Skeleton,
  EmptyState,
  EstadoVazio
} from "../../components/ui/primitives";
import { api } from "../../lib/api";
import { filtrarPorNome } from "../../lib/busca";
import {
  brPt,
  gradeColor,
  gradeColorTexto,
  fmtTempo,
  initials,
  avatarColor,
  extrairSerie
} from "../../lib/format";
import { periodoInicial } from "../../lib/periodo";
import { useAsync } from "../../lib/useAsync";
import { cn, scrollPainelTopo } from "../../lib/utils";
import { TurmaPainelView } from "../professor/TurmaPainel";

export function AdminPainelView({
  escolaId,
  escolaNome,
  ano,
  periodo,
  onMeta,
  onSelectTurma,
  onBreadcrumb
}) {
  const range = periodo ?? periodoInicial();
  const [turmaSel, setTurmaSel] = useState(null);
  function selecionarTurmaInterna(t) {
    setTurmaSel(t);
    scrollPainelTopo();
  }
  function voltarEscola() {
    setTurmaSel(null);
    scrollPainelTopo();
  }
  const selecionarTurma = onSelectTurma ?? selecionarTurmaInterna;

  // Reporta a trilha escola › turma pro cabeçalho principal — mesmo padrão do
  // GestorPainel. Quando o Gestor controla a navegação por fora (onSelectTurma
  // informado), turmaSel nunca é setado aqui e onBreadcrumb não é passado, então
  // isso vira um no-op inofensivo.
  useEffect(() => {
    const crumbs = [
      {
        label: escolaNome ?? "Escola",
        nivel: "escola",
        onClick: turmaSel ? voltarEscola : undefined
      }
    ];

    if (turmaSel) crumbs.push({ label: turmaSel.turma, nivel: "turma" });
    onBreadcrumb?.(crumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escolaNome, turmaSel, onBreadcrumb]);

  const kpis = useAsync(
    () => api.escolaKpis(escolaId, ano, range.inicio, range.fim),
    [escolaId, ano, range.inicio, range.fim]
  );
  const evolucao = useAsync(
    () => api.escolaEvolucao(escolaId, ano, range.inicio, range.fim),
    [escolaId, ano, range.inicio, range.fim]
  );
  const turmas = useAsync(
    () => api.escolaTurmas(escolaId, ano, range.inicio, range.fim),
    [escolaId, ano, range.inicio, range.fim]
  );
  // Filtro de série no ranking de turmas — a série não vem como campo próprio
  // da origem (só o nome livre da turma), então é extraída do prefixo do nome
  // (ver extrairSerie). Zera quando a escola troca, pra não herdar uma série
  // que talvez não exista na escola seguinte.
  const [serieSel, setSerieSel] = useState("");
  useEffect(() => setSerieSel(""), [escolaId]);
  const series = useMemo(
    () =>
      Array.from(
        new Set((turmas.data ?? []).map(t => extrairSerie(t.turma)))
      ).sort(),
    [turmas.data]
  );
  // Busca por nome, somada ao filtro de série — os dois se aplicam juntos, na
  // ordem série → nome. Zera junto com a série quando a escola troca: um termo
  // herdado esconderia a lista inteira da escola seguinte sem explicar por quê.
  const [buscaTurma, setBuscaTurma] = useState("");
  useEffect(() => setBuscaTurma(""), [escolaId]);

  // Turmas vêm completas de /escola/:id/turmas (sem paginação), então o filtro
  // é local. Ver a nota sobre os dois modos em components/BuscaNome.jsx.
  const turmasFiltradas = useMemo(() => {
    const porSerie = serieSel
      ? (turmas.data ?? []).filter(t => extrairSerie(t.turma) === serieSel)
      : turmas.data ?? [];

    return filtrarPorNome(porSerie, buscaTurma, ["turma"]);
  }, [turmas.data, serieSel, buscaTurma]);
  const disciplinas = useAsync(
    () => api.disciplinasDesempenho(escolaId, ano),
    [escolaId, ano]
  );
  const inativos = useAsync(() => api.escolaInativos(escolaId), [escolaId]);

  // Bubbla contagens/anos pro topbar (seletor de ano letivo + subtítulo).
  useEffect(() => {
    const d = kpis.data;
    if (d)
      onMeta?.({
        alunos: d.alunos,
        turmas: d.turmas,
        anos: d.anos,
        ano: d.ano
      });
  }, [kpis.data, onMeta]);

  if (kpis.loading) return <PainelSkeleton />;
  if (kpis.error)
    return (
      <EmptyState
        title="Não foi possível carregar a escola"
        hint={kpis.error}
      />
    );
  const k = kpis.data;

  // Turma selecionada (sem onSelectTurma externo): substitui o painel inteiro
  // pelo mesmo painel do professor, igual ao Gestor faz — em vez de um drawer
  // por cima.
  if (turmaSel) {
    return (
      <TurmaPainelView
        turmaId={turmaSel.id}
        ano={ano ?? k.ano ?? undefined}
        periodo={periodo}
      />
    );
  }

  return (
    <div className="space-y-7">
      {/* ── Visão geral ─────────────────────────────────────────────────── */}
      <section id="visao-geral" className="scroll-mt-4">
        <SectionHead title="Visão geral" />
        <div className="grid gap-4 lg:grid-cols-[2.4fr_1fr]">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            <KpiCard
              label="Nota média da escola"
              value={brPt(k.nota_media)}
              suffix="/10"
              accent="var(--color-nota)"
              valueColor={gradeColor(k.nota_media)}
              icon={<Star size={16} />}
            />

            <KpiCard
              label="Total de alunos"
              value={k.alunos}
              accent="var(--color-data)"
              sub="matriculados na escola"
              icon={<Users size={16} />}
            />

            <KpiCard
              label="Tempo online"
              value={fmtTempo(k.tempo_total_seg)}
              accent="var(--color-chart-6)"
              sub="soma do tempo online dos alunos"
              icon={<Clock size={16} />}
            />

            <KpiCard
              label="Taxa de conclusão"
              value={
                k.taxa_conclusao == null ? "–" : Math.round(k.taxa_conclusao)
              }
              suffix="%"
              accent="var(--color-good)"
              icon={<CheckCircle2 size={16} />}
            />

            <KpiCard
              label="Alunos inativos"
              value={k.inativos}
              accent="var(--color-bad)"
              sub={`sem acessar a plataforma há mais de ${
                k.inativos_dias ?? 7
              } dias`}
              icon={<UserX size={16} />}
            />

            {/* KPI "Nunca acessaram" removido a pedido do Kilson em 04/08/2026 —
                não quer o card por enquanto, pode voltar a usar depois. O
                backend continua calculando k.nunca_acessaram. */}

            <KpiCard
              label="Score de engajamento"
              value={
                k.engajamento_medio == null
                  ? "–"
                  : Math.round(k.engajamento_medio)
              }
              suffix={k.engajamento_medio != null ? "%" : undefined}
              accent="var(--color-chart-5)"
              sub="média da escola no período"
              icon={<Activity size={16} />}
            />
          </div>
          <FaixaDonutCard faixas={k.faixas} />
        </div>
      </section>

      {/* ── Evolução temporal ───────────────────────────────────────────── */}
      <section id="evolucao-temporal" className="scroll-mt-4">
        <SectionHead title="Evolução temporal da escola" sub="média mensal" />
        <EvolucaoTemporal
          serie={evolucao.data ?? []}
          loading={evolucao.loading}
          vazioEngHint="Nenhum score registrado para a escola no período."
        />
      </section>

      {/* ── Ranking de turmas ───────────────────────────────────────────── */}
      <section id="ranking-turmas" className="scroll-mt-4">
        <SectionHead
          title="Ranking de turmas"
          sub="clique numa turma para ver o detalhamento"
        />
        <Card>
          {turmas.data && turmas.data.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <BuscaNome
                id="ie-busca-turma-escola"
                value={buscaTurma}
                onChange={setBuscaTurma}
                placeholder="Buscar turma…"
                total={turmas.data.length}
                filtrado={turmasFiltradas.length}
                rotulo="turma"
                className=""
              />
              <SerieFiltro
                series={series}
                value={serieSel}
                onChange={setSerieSel}
              />
            </div>
          )}

          {turmas.loading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : turmasFiltradas.length ? (
            <RankingTurmas
              linhas={turmasFiltradas}
              onSelect={selecionarTurma}
            />
          ) : buscaTurma ? (
            <SemResultadoBusca termo={buscaTurma} item="turma" />
          ) : (
            <EstadoVazio
              icon={<School size={20} />}
              titulo="Nenhuma turma com alunos"
              hint={
                serieSel
                  ? "Nenhuma turma dessa série nesta escola."
                  : "Nenhuma turma com alunos matriculados nesta escola."
              }
            />
          )}
        </Card>
      </section>

      {/* ── Desempenho por disciplina ───────────────────────────────────── */}
      <section id="desempenho-disciplina" className="scroll-mt-4">
        <SectionHead
          title="Desempenho por disciplina"
          sub="nota média · escala 0–10"
        />
        <Card>
          {disciplinas.loading ? (
            <Skeleton className="h-[220px] w-full" />
          ) : disciplinas.data?.length ? (
            <NotaBarraLista
              data={disciplinas.data.map(d => ({
                nome: d.disciplina,
                nota: d.media ?? 0
              }))}
              labelKey="nome"
              valueKey="nota"
              labelWidth={120}
            />
          ) : (
            <EstadoVazio
              icon={<BarChart3 size={20} />}
              titulo="Sem avaliações suficientes"
              hint="Nenhuma disciplina com nota registrada nesta escola."
            />
          )}
        </Card>
      </section>

      {/* ── Alunos inativos ─────────────────────────────────────────────── */}
      <section id="alunos-inativos" className="scroll-mt-4">
        <SectionHead
          title="Alunos inativos"
          sub={`sem acessar a plataforma há mais de ${
            k?.inativos_dias ?? 7
          } dias, do mais ausente para o menos`}
        />
        <Card>
          {inativos.loading ? (
            <Skeleton className="h-[220px] w-full" />
          ) : inativos.data?.length ? (
            <TabelaInativos linhas={inativos.data} />
          ) : (
            <EstadoVazio
              icon={<CheckCircle2 size={20} />}
              titulo="Nenhum aluno inativo"
              hint="Todos os alunos com acesso registrado entraram na plataforma na última semana."
              tom="positivo"
            />
          )}
        </Card>
      </section>
    </div>
  );
}

const SERIE_POPOVER_WIDTH = 180;

/** Filtro de série (ranking de turmas) — botão compacto (só ícone) que abre um
 * popover com as séries encontradas. O painel é renderizado via portal em
 * document.body e posicionado por coordenadas reais do botão (position: fixed)
 * — mesma técnica do HoverPopover: o Card em volta tem `hover:-translate-y-0.5`
 * (transform), que cria um stacking context e prendia o popover atrás das
 * seções seguintes (Desempenho por disciplina) apesar do z-50. */
function SerieFiltro({ series, value, onChange }) {
  const [aberto, setAberto] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });

  useLayoutEffect(() => {
    if (!aberto) return;
    const btn = btnRef.current;
    const panel = panelRef.current;
    if (!btn || !panel) return;
    const gap = 6;
    const rect = btn.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= panel.offsetHeight + gap
        ? rect.bottom + gap
        : rect.top - gap - panel.offsetHeight;
    const left = Math.max(
      8,
      Math.min(
        rect.right - SERIE_POPOVER_WIDTH,
        window.innerWidth - SERIE_POPOVER_WIDTH - 8
      )
    );
    setPos({ left, top: Math.max(8, top), ready: true });
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    function onDoc(e) {
      if (
        btnRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      )
        return;
      setAberto(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setAberto(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [aberto]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setAberto(v => !v)}
        aria-expanded={aberto}
        aria-label="Filtrar por série"
        title={value ? `Série: ${value}` : "Filtrar por série"}
        className={cn(
          "grid h-9 w-9 place-items-center rounded-xl border transition-all",
          "border-transparent bg-[var(--color-surface-2)]/60 text-[var(--color-ink-soft)]",
          "hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
          (aberto || value) &&
            "border-[var(--color-brand)] bg-[var(--color-surface-2)] text-[var(--color-ink)] shadow-sm"
        )}
      >
        <Layers
          size={15}
          className={value ? "text-[var(--color-brand)]" : undefined}
        />
      </button>

      {aberto && (
        <PortalIE>
          <div
            ref={panelRef}
            className={cn(
              "rounded-2xl border p-1.5",
              "border-[var(--color-border-strong)] bg-[var(--color-surface)] shadow-2xl shadow-black/20"
            )}
            style={{
              // idem HoverPopover: `z-50` ficaria atrás do header do AVA.
              // minHeight/overflow inline porque é o que faz a lista rolar.
              position: "fixed",
              zIndex: 1036,
              maxHeight: 280,
              overflowY: "auto",
              width: SERIE_POPOVER_WIDTH,
              left: pos.left,
              top: pos.top,
              visibility: pos.ready ? "visible" : "hidden"
            }}
          >
            <button
              onClick={() => {
                onChange("");
                setAberto(false);
              }}
              className={cn(
                "block w-full rounded-lg px-2.5 py-1.5 text-left text-[12.5px] transition-colors hover:bg-[var(--color-surface-2)]",
                !value
                  ? "font-semibold text-[var(--color-brand)]"
                  : "text-[var(--color-ink-soft)]"
              )}
            >
              Todas as séries
            </button>
            {series.map(s => (
              <button
                key={s}
                onClick={() => {
                  onChange(s);
                  setAberto(false);
                }}
                className={cn(
                  "block w-full rounded-lg px-2.5 py-1.5 text-left text-[12.5px] transition-colors hover:bg-[var(--color-surface-2)]",
                  value === s
                    ? "font-semibold text-[var(--color-brand)]"
                    : "text-[var(--color-ink-soft)]"
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </PortalIE>
      )}
    </>
  );
}

/* ── Ranking de turmas (medalhas) ─────────────────────────────────────── */
function RankingTurmas({ linhas, onSelect }) {
  const colunas = [
    {
      key: "turma",
      label: "Turma",
      render: t => (
        <span className="font-medium text-[var(--color-ink)]">{t.turma}</span>
      )
    },
    {
      key: "turno",
      label: "Turno",
      render: t => (
        <span className="text-[var(--color-ink-soft)]">{t.turno ?? "—"}</span>
      )
    },
    {
      key: "nota",
      label: "Nota",
      num: true,
      render: t =>
        t.nota == null ? (
          <span className="text-[var(--color-ink-faint)]">–</span>
        ) : (
          <NotaCell dados={t} />
        )
    },
    {
      key: "engajamento",
      label: "Engajamento",
      num: true,
      render: t =>
        t.engajamento == null ? (
          <span className="tnum text-[var(--color-ink-faint)]">–</span>
        ) : (
          <EngajamentoCell dados={t} />
        )
    },
    {
      key: "conclusao",
      label: "Conclusão",
      num: true,
      render: t => (
        <span className="tnum text-[var(--color-ink-soft)]">
          {t.conclusao == null ? "–" : `${Math.round(t.conclusao)}%`}
        </span>
      )
    },
    {
      key: "atividades",
      label: "Atividades",
      num: true,
      render: t => (
        <span className="tnum text-[var(--color-ink-soft)]">
          {t.atividades.toLocaleString("pt-BR")}
        </span>
      )
    },
    {
      key: "tempo",
      label: "Tempo",
      num: true,
      render: t => (
        <span className="tnum text-[var(--color-ink-soft)]">
          {fmtTempo(t.tempo_seg)}
        </span>
      )
    }
  ];

  return (
    <RankingTabela
      linhas={linhas}
      colunas={colunas}
      rowKey={t => t.id}
      onRowClick={onSelect}
      rank
    />
  );
}

function cnTh(num) {
  return [
    "whitespace-nowrap px-2.5 py-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-faint)]",
    num ? "text-right" : "text-left"
  ].join(" ");
}

/* ── Tabela de alunos inativos ─────────────────────────────────────────── */
function TabelaInativos({ linhas }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-[var(--color-border-strong)]">
            <th className={cnTh(false)}>Aluno</th>
            <th className={cnTh(false)}>Turma</th>
            <th className={cnTh(true)}>Última nota</th>
            {/* A coluna `dias` do backend passou a ser dias sem ACESSO
                (dias_desde_ultimo_acesso), não dias sem avaliação — o rótulo
                antigo descrevia a métrica que a correção de 04/08 substituiu. */}
            <th className={cnTh(true)}>Sem acessar há</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((a, i) => (
            <tr
              key={`${a.aluno}-${a.turma}-${i}`}
              className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <td className="px-2.5 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span
                    className="grid h-7 w-7 flex-none place-items-center rounded-full text-[10px] font-bold text-white"
                    style={{ background: avatarColor(i) }}
                  >
                    {initials(a.aluno)}
                  </span>
                  <span className="font-medium text-[var(--color-ink)]">
                    {a.aluno}
                  </span>
                </div>
              </td>
              <td className="px-2.5 py-2.5 text-[var(--color-ink-soft)]">
                {a.turma}
              </td>
              <td className="px-2.5 py-2.5 text-right">
                {a.nota == null ? (
                  <span className="text-[var(--color-ink-faint)]">–</span>
                ) : (
                  <span
                    className="tnum font-bold"
                    style={{ color: gradeColorTexto(a.nota) }}
                  >
                    {brPt(a.nota)}
                  </span>
                )}
              </td>
              <td className="tnum px-2.5 py-2.5 text-right text-[var(--color-warn-ink)]">
                {a.dias == null ? "–" : `${a.dias} dias`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PainelSkeleton() {
  return (
    <div className="space-y-7">
      <div className="grid gap-4 lg:grid-cols-[2.4fr_1fr]">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
        <Skeleton className="h-[104px] lg:h-auto" />
      </div>
      <Skeleton className="h-[260px]" />
      <Skeleton className="h-[320px]" />
    </div>
  );
}
